import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { EASE } from "./Reveal";

const LOGO_SRC = "/assets/logo-gold-film.webp";
const FULL_MS = 3450;
const REDUCED_MS = 850;

// Luxury brand-film opening built from the geometry of the official logo:
// stillness → champagne arc draws → diagonals draw → the sketch resolves into
// the official wordmark via an architectural wipe → brand statement → hold →
// the logo travels to its navigation position as the hero reveals.
const Intro = () => {
  const [show, setShow] = useState(false);
  const [reduced, setReduced] = useState(false);
  const [target, setTarget] = useState({ x: 0, y: 0, s: 0.12, stmtTop: 0 });

  useEffect(() => {
    if (sessionStorage.getItem("aviah_intro_seen")) return;
    const isReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const W = window.innerWidth;
    const H = window.innerHeight;
    const displayW = Math.min(W * 0.72, 520);
    const displayH = displayW * (918 / 1985); // cropped wordmark aspect
    const headerLogo = document.querySelector('[data-testid="header-logo"] img');
    const rect = headerLogo ? headerLogo.getBoundingClientRect() : null;
    const cx = rect ? rect.left + rect.width / 2 : W / 2;
    const cy = rect ? rect.top + rect.height / 2 : 40;
    setTarget({
      x: cx - W / 2,
      y: cy - H / 2,
      s: rect ? rect.width / displayW : 0.12,
      stmtTop: H / 2 + displayH / 2 + 30,
    });
    setReduced(isReduced);
    setShow(true);
    document.body.style.overflow = "hidden";
    const timer = setTimeout(() => {
      sessionStorage.setItem("aviah_intro_seen", "1");
      document.body.style.overflow = "";
      setShow(false);
    }, isReduced ? REDUCED_MS : FULL_MS);
    return () => {
      clearTimeout(timer);
      document.body.style.overflow = "";
    };
  }, []);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className="fixed inset-0 z-[90]"
          data-testid="intro-overlay"
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          role="presentation"
        >
          {/* carbon backdrop — lifts as the logo travels to the header */}
          <motion.div
            className="absolute inset-0 bg-[#171717]"
            initial={{ opacity: 1 }}
            animate={{ opacity: 0 }}
            transition={reduced ? { delay: 0.55, duration: 0.3 } : { delay: 2.9, duration: 0.55, ease: "easeInOut" }}
          />

          {reduced ? (
            <div className="relative flex h-full items-center justify-center">
              <motion.img
                src={LOGO_SRC}
                alt="Aviah Financial"
                className="w-[min(64vw,320px)]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              />
            </div>
          ) : (
            <>
              <motion.div
                className="absolute inset-0 flex items-center justify-center"
                initial={{ x: 0, y: 0, scale: 1 }}
                animate={{ x: target.x, y: target.y, scale: target.s }}
                transition={{ delay: 2.7, duration: 0.75, ease: EASE }}
              >
                <motion.div
                  className="relative flex items-center justify-center"
                  animate={{ opacity: [1, 1, 0] }}
                  transition={{ delay: 2.7, duration: 0.75, times: [0, 0.78, 1] }}
                >
                  {/* the geometry constructs itself first */}
                  <motion.svg
                    viewBox="0 0 120 100"
                    className="absolute h-44 w-52 sm:h-60 sm:w-72"
                    fill="none"
                    aria-hidden="true"
                    initial={{ opacity: 1 }}
                    animate={{ opacity: 0 }}
                    transition={{ delay: 1.55, duration: 0.4 }}
                  >
                    <motion.path
                      d="M 92 22 A 45 45 0 1 0 92 78"
                      stroke="#C6A46A"
                      strokeWidth="1.4"
                      strokeLinecap="round"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ delay: 0.3, duration: 0.85, ease: "easeInOut" }}
                    />
                    <motion.line
                      x1="66" y1="94" x2="90" y2="6"
                      stroke="#C6A46A"
                      strokeWidth="1.4"
                      strokeLinecap="round"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ delay: 1.0, duration: 0.45, ease: "easeInOut" }}
                    />
                    <motion.line
                      x1="82" y1="94" x2="106" y2="6"
                      stroke="#C6A46A"
                      strokeWidth="1.4"
                      strokeLinecap="round"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ delay: 1.15, duration: 0.45, ease: "easeInOut" }}
                    />
                  </motion.svg>

                  {/* the official wordmark, revealed by an architectural wipe */}
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4, duration: 0.1 }}>
                    <motion.img
                      src={LOGO_SRC}
                      alt="Aviah Financial"
                      className="w-[min(72vw,520px)]"
                      initial={{ clipPath: "inset(0% 100% 0% 0%)" }}
                      animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
                      transition={{ delay: 1.42, duration: 0.95, ease: EASE }}
                    />
                  </motion.div>
                </motion.div>
              </motion.div>

              {/* brand statement — fixed beneath the wordmark, lifts away before the logo travels */}
              {target.stmtTop > 0 && (
                <motion.p
                  className="absolute left-0 right-0 text-center text-[0.62rem] font-light uppercase tracking-[0.42em] text-[#C6A46A]"
                  style={{ top: target.stmtTop }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: [0, 1, 1, 0] }}
                  transition={{ delay: 2.1, duration: 0.85, times: [0, 0.35, 0.75, 1] }}
                >
                  Clarity&nbsp;&nbsp;•&nbsp;&nbsp;Strategy&nbsp;&nbsp;•&nbsp;&nbsp;Legacy
                </motion.p>
              )}
            </>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Intro;
